// zr-core/identity — per-participant cryptographic identity for the P2P path.
//
// WebRTC already gives every connection an ephemeral, OS-assigned UDP/TCP port per ICE
// candidate and a self-signed DTLS certificate — so "dynamic ephemeral ports" are native
// and need no management. What it does NOT give you is a *stable, verifiable* identity for
// a participant across reconnects, nor protection against a malicious signaling relay
// swapping a peer's DTLS fingerprint (a MITM). This module adds that: each participant
// holds an ECDH P-256 keypair, announces its public key, and the two sides derive:
//   • a short hex FINGERPRINT of each public key (compact identity), and
//   • a 60-digit SAFETY NUMBER from both keys (WhatsApp-style, compare out-of-band to
//     confirm there's no MITM), and
//   • optionally a shared secret via ECDH to key end-to-end media encryption WITHOUT any
//     pre-shared password (see deriveSharedKeyBits).
//
// Pure WebCrypto, so it runs in browsers, React Native (with a crypto polyfill) and Node
// 18+ — and is unit-testable without a DOM.

function getSubtle(): SubtleCrypto {
  const c = (globalThis as any).crypto;
  if (!c?.subtle) throw new Error('Peer identity requires WebCrypto (crypto.subtle).');
  return c.subtle;
}

function bs(buf: Uint8Array | ArrayBuffer): BufferSource {
  return buf as unknown as BufferSource;
}

/** True when the runtime can mint peer identities (WebCrypto ECDH available). */
export function isIdentitySupported(): boolean {
  return !!(globalThis as any).crypto?.subtle;
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i], b1 = bytes[i + 1], b2 = bytes[i + 2];
    out += B64URL[b0 >> 2];
    out += B64URL[((b0 & 3) << 4) | ((b1 ?? 0) >> 4)];
    out += i + 1 < bytes.length ? B64URL[((b1 & 15) << 2) | ((b2 ?? 0) >> 6)] : '';
    out += i + 2 < bytes.length ? B64URL[b2 & 63] : '';
  }
  return out;
}

export function fromBase64Url(str: string): Uint8Array {
  const lookup = new Map<string, number>();
  for (let i = 0; i < B64URL.length; i++) lookup.set(B64URL[i], i);
  const out: number[] = [];
  for (let i = 0; i < str.length; i += 4) {
    const c0 = lookup.get(str[i]) ?? 0;
    const c1 = lookup.get(str[i + 1]) ?? 0;
    const c2 = lookup.get(str[i + 2]);
    const c3 = lookup.get(str[i + 3]);
    out.push((c0 << 2) | (c1 >> 4));
    if (c2 !== undefined) out.push(((c1 & 15) << 4) | (c2 >> 2));
    if (c2 !== undefined && c3 !== undefined) out.push(((c2 & 3) << 6) | c3);
  }
  return new Uint8Array(out);
}

/** A participant's identity: a keypair plus the exported (shareable) public key bytes. */
export interface PeerIdentity {
  keyPair: CryptoKeyPair;
  /** Raw uncompressed public key (65 bytes for P-256). */
  publicKeyRaw: Uint8Array;
  /** base64url of publicKeyRaw — the form announced over signaling. */
  publicKey: string;
}

/** Generate a fresh ECDH P-256 identity for this participant/session. */
export async function generatePeerIdentity(): Promise<PeerIdentity> {
  const subtle = getSubtle();
  const keyPair = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ])) as CryptoKeyPair;
  const raw = new Uint8Array(await subtle.exportKey('raw', keyPair.publicKey));
  return { keyPair, publicKeyRaw: raw, publicKey: toBase64Url(raw) };
}

/** Import a peer's announced public key (base64url) into a CryptoKey for ECDH. */
export async function importPeerPublicKey(publicKey: string): Promise<CryptoKey> {
  return getSubtle().importKey(
    'raw',
    bs(fromBase64Url(publicKey)),
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    [],
  );
}

/**
 * ECDH → 32 shared bytes between our private key and a peer's public key. Both sides compute
 * the SAME value, so it can key end-to-end media encryption with no pre-shared password.
 */
export async function deriveSharedKeyBits(
  selfPrivate: CryptoKey,
  peerPublicKey: string,
): Promise<Uint8Array> {
  const peerKey = await importPeerPublicKey(peerPublicKey);
  const bits = await getSubtle().deriveBits({ name: 'ECDH', public: peerKey }, selfPrivate, 256);
  // Hash the raw ECDH output so the key is uniformly distributed (lightweight KDF).
  const digest = await getSubtle().digest('SHA-256', bits);
  return new Uint8Array(digest);
}

/** Short, stable hex fingerprint (first 8 bytes of SHA-256) of a public key, grouped. */
export async function fingerprint(publicKey: string): Promise<string> {
  const digest = new Uint8Array(await getSubtle().digest('SHA-256', bs(fromBase64Url(publicKey))));
  const hex = Array.from(digest.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return (hex.match(/.{1,4}/g) || []).join(' ').toUpperCase();
}

/**
 * A 60-digit safety number derived from BOTH public keys (order-independent), grouped into
 * 12 blocks of 5 — the WhatsApp/Signal pattern. Two peers showing the same number out-of-band
 * confirms no MITM swapped a key in the signaling relay.
 */
export async function safetyNumber(publicKeyA: string, publicKeyB: string): Promise<string> {
  // Order-independent: sort the two keys so both peers compute the same input.
  const [k1, k2] = [publicKeyA, publicKeyB].sort();
  const material = new TextEncoder().encode(`${k1}|${k2}`);
  const digest = new Uint8Array(await getSubtle().digest('SHA-512', bs(material)));
  // Map 30 bytes → 60 digits (each byte → 2 decimal digits).
  let digits = '';
  for (let i = 0; i < 30; i++) digits += (digest[i] % 100).toString().padStart(2, '0');
  return (digits.match(/.{1,5}/g) || []).join(' ');
}
